import React, { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronUp, MessageCircle, Clock, Zap, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navigation from './Navigation';
import Footer from './Footer';

interface FaqItemProps {
  question: string;
  answer: React.ReactNode;
  icon?: React.ReactNode;
}

const FaqItem: React.FC<FaqItemProps> = ({ question, answer, icon }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mb-4 border border-line rounded-none overflow-hidden transition-all duration-300 hover:shadow-md">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-6 py-5 flex items-center justify-between text-left transition-colors duration-300 ${
          isOpen ? 'bg-surface' : 'bg-surface hover:bg-surface'
        }`}
      >
        <div className="flex items-center gap-4 flex-1">
          {icon && <div className="text-ink flex-shrink-0">{icon}</div>}
          <h3 className="text-lg font-semibold text-ink">{question}</h3>
        </div>
        <div className="text-ink flex-shrink-0 ml-4">
          {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </div>
      </button>
      {isOpen && (
        <div className="px-6 py-5 bg-surface border-t border-line">
          <div className="prose prose-neutral max-w-none text-muted space-y-4">
            {answer}
          </div>
        </div>
      )}
    </div>
  );
};

const FaqPage: React.FC = () => {
  const navigate = useNavigate();

  const faqs: FaqItemProps[] = [
    {
      question: 'What is DressUp AI?',
      answer: (
        <>
          <p>
            DressUp is a fashion marketplace that builds itself around you. It reads your coloring, your size and your taste, then shows you only the items that fit all three.
          </p>
          <p>
            Think of it as having a personal stylist in your pocket, available 24/7 to help you look and feel your best for any occasion.
          </p>
        </>
      ),
      icon: <Zap className="h-6 w-6" />,
    },
    {
      question: 'How does the AI styling technology work?',
      answer: (
        <>
          <p>
            Our AI styling technology works through a combination of computer vision, machine learning, and fashion expertise:
          </p>
          <ol className="list-decimal list-inside space-y-2">
            <li>
              <strong>Style Analysis:</strong> When you join DressUp, we'll ask you about your style preferences, favorite colors, patterns, and brands.
            </li>
            <li>
              <strong>Wardrobe Digitization:</strong> Upload photos of your existing clothes to create a digital wardrobe.
            </li>
            <li>
              <strong>Trend Integration:</strong> Our AI stays updated with the latest fashion trends from around the world.
            </li>
            <li>
              <strong>Smart Recommendations:</strong> Based on all this data, we generate personalized outfit recommendations for different occasions.
            </li>
          </ol>
          <p>
            The more you use DressUp, the better it understands your style, continuously improving its recommendations to match your preferences.
          </p>
        </>
      ),
      icon: <HelpCircle className="h-6 w-6" />,
    },
    {
      question: 'When will DressUp be available?',
      answer: (
        <>
          <p>
            DressUp is currently in development and will be launched in phases:
          </p>
          <ul className="list-none space-y-2">
            <li className="flex gap-3">
              <span className="text-ink font-bold">•</span>
              <div>
                <strong>Beta Access:</strong> Summer 2025 - Limited access for waitlist members who sign up early
              </div>
            </li>
            <li className="flex gap-3">
              <span className="text-ink font-bold">•</span>
              <div>
                <strong>Public Launch:</strong> Fall 2025 - Full release with complete feature set
              </div>
            </li>
          </ul>
          <p>
            By joining our waitlist now, you'll secure early access and be among the first to experience DressUp's AI styling capabilities. Early users will also have the opportunity to provide feedback that shapes the final product.
          </p>
        </>
      ),
      icon: <Clock className="h-6 w-6" />,
    },
    {
      question: 'How does DressUp protect my privacy?',
      answer: (
        <>
          <p>
            We take your privacy seriously. Here's how we protect your data:
          </p>
          <ul className="list-none space-y-2">
            <li className="flex gap-3">
              <span className="text-ink font-bold">•</span>
              Your wardrobe images and personal style information are stored securely and never shared without your consent
            </li>
            <li className="flex gap-3">
              <span className="text-ink font-bold">•</span>
              All data is encrypted both in transit and at rest
            </li>
            <li className="flex gap-3">
              <span className="text-ink font-bold">•</span>
              You maintain full control over your data and can delete it at any time
            </li>
            <li className="flex gap-3">
              <span className="text-ink font-bold">•</span>
              We do not sell your personal information to third parties
            </li>
          </ul>
          <p>
            For more detailed information, please review our{' '}
            <button
              onClick={() => navigate('/privacy-policy')}
              className="text-ink hover:underline font-semibold"
            >
              Privacy Policy
            </button>
            .
          </p>
        </>
      ),
      icon: <Heart className="h-6 w-6" />,
    },
    {
      question: 'How can I contact customer support?',
      answer: (
        <>
          <p>
            There are several ways to reach our customer support team:
          </p>
          <ul className="list-none space-y-2">
            <li className="flex gap-3">
              <span className="text-ink font-bold">•</span>
              <div>
                <strong>Email:</strong> abdulbasit@thedressup.co
              </div>
            </li>
            <li className="flex gap-3">
              <span className="text-ink font-bold">•</span>
              <div>
                <strong>In-App Chat:</strong> Once the app launches, you'll have access to 24/7 chat support
              </div>
            </li>
            <li className="flex gap-3">
              <span className="text-ink font-bold">•</span>
              <div>
                <strong>Help Center:</strong> Browse our comprehensive knowledge base at help.dressup.ai
              </div>
            </li>
          </ul>
          <p>
            Our support team is available Monday through Friday, 9am-6pm PST. We aim to respond to all inquiries within 24 hours.
          </p>
        </>
      ),
      icon: <MessageCircle className="h-6 w-6" />,
    },
  ];

  return (
    <div className="min-h-screen bg-paper">
      <Navigation />

      <main className="max-w-4xl mx-auto px-6 py-24">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-panel rounded-none mb-6">
            <HelpCircle className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-ink mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-muted text-lg max-w-2xl mx-auto">
            Everything you need to know about DressUp AI and how it will transform your wardrobe experience.
          </p>
        </div>

        {/* FAQ Items */}
        <div className="mb-16">
          {faqs.map((faq, index) => (
            <FaqItem key={index} question={faq.question} answer={faq.answer} icon={faq.icon} />
          ))}
        </div>

        {/* Contact CTA */}
        <div className="bg-surface border-l-4 border-accent rounded-none p-8 text-center">
          <h2 className="text-2xl font-bold text-ink mb-4">
            Still have questions?
          </h2>
          <p className="text-muted mb-6 max-w-xl mx-auto">
            Our team is here to help with any additional questions you might have.
          </p>
          <button
            onClick={() => navigate('/contact-us')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-panel text-white font-semibold rounded-none transition-all duration-300 hover:shadow-lg hover:scale-105"
          >
            <MessageCircle className="h-5 w-5" />
            Contact Us
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FaqPage;
